import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Badge } from "./components/ui/badge";
import { CheckCircle, AlertTriangle, Lock, Key, Hash, Link as LinkIcon } from "lucide-react";

const AADESettings = ({ onSave, existingSettings }) => {
  const [settings, setSettings] = useState({
    afm: existingSettings?.afm || "",
    userId: existingSettings?.userId || "",
    subscriptionKey: existingSettings?.subscriptionKey || "",
    environment: existingSettings?.environment || "production" // or "sandbox"
  });

  const [testStatus, setTestStatus] = useState(null); // null, 'testing', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const [isConnected, setIsConnected] = useState(existingSettings?.isConnected || false);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setTestStatus(null); // Reset test status on change
  };

  const testConnection = async () => {
    setTestStatus('testing');
    setErrorMessage("");

    try {
      // Call backend API to test AADE connection
      const response = await fetch('https://nomokratia.ai/api/aade/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setTestStatus('success');
        setIsConnected(true);
      } else {
        setTestStatus('error');
        setErrorMessage(data.error || 'Αποτυχία σύνδεσης με AADE');
        setIsConnected(false);
      }
    } catch (error) {
      setTestStatus('error');
      setErrorMessage('Σφάλμα δικτύου. Δοκιμάστε ξανά.');
      setIsConnected(false);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave({ ...settings, isConnected });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-2xl font-bold text-blue-900 mb-2">
                🇬🇷 AADE myDATA Integration
              </h3>
              <p className="text-blue-700 mb-3">
                Αυτόματη αποστολή τιμολογίων στην ΑΑΔΕ σε πραγματικό χρόνο
              </p>
              <div className="flex items-center space-x-2">
                {isConnected ? (
                  <Badge className="bg-green-600">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Συνδεδεμένο
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-orange-500 text-orange-700">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Μη Συνδεδεμένο
                  </Badge>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Περιβάλλον</p>
              <Badge className={settings.environment === 'production' ? 'bg-green-600' : 'bg-yellow-600'}>
                {settings.environment === 'production' ? 'Παραγωγής' : 'Δοκιμών'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle>Στοιχεία Σύνδεσης ΑΑΔΕ</CardTitle>
          <CardDescription>
            Εισάγετε τα credentials σας από το myDATA Developer Portal
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Security Notice */}
          <Alert className="border-purple-200 bg-purple-50">
            <Lock className="h-4 w-4 text-purple-600" />
            <AlertDescription className="text-purple-800 text-sm">
              <strong>Ασφάλεια:</strong> Τα στοιχεία σας αποθηκεύονται κρυπτογραφημένα και είναι προσβάσιμα μόνο από εσάς.
            </AlertDescription>
          </Alert>

          {/* Environment Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Περιβάλλον API
            </label>
            <div className="flex space-x-3">
              <Button
                variant={settings.environment === 'sandbox' ? 'default' : 'outline'}
                onClick={() => handleChange('environment', 'sandbox')}
                className="flex-1"
              >
                Δοκιμών (Sandbox)
              </Button>
              <Button
                variant={settings.environment === 'production' ? 'default' : 'outline'}
                onClick={() => handleChange('environment', 'production')}
                className="flex-1"
              >
                Παραγωγής (Production)
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {settings.environment === 'sandbox' 
                ? '⚠️ Χρήση για δοκιμές - δεν καταχωρούνται πραγματικά τιμολόγια'
                : '✅ Παραγωγικό περιβάλλον - πραγματική καταχώρηση στην ΑΑΔΕ'
              }
            </p>
          </div>

          {/* AFM Field */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Hash className="h-4 w-4 mr-2 text-gray-600" />
              ΑΦΜ (Αριθμός Φορολογικού Μητρώου)
            </label>
            <Input
              type="text"
              placeholder="123456789"
              value={settings.afm}
              onChange={(e) => handleChange('afm', e.target.value)}
              maxLength={9}
              className="font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              Το 9ψήφιο ΑΦΜ της επιχείρησής σας
            </p>
          </div>

          {/* User ID Field */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Key className="h-4 w-4 mr-2 text-gray-600" />
              User ID
            </label>
            <Input
              type="text"
              placeholder="your-username"
              value={settings.userId}
              onChange={(e) => handleChange('userId', e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">
              Το User ID από το myDATA Developer Portal
            </p>
          </div>

          {/* Subscription Key Field */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Lock className="h-4 w-4 mr-2 text-gray-600" />
              Subscription Key
            </label>
            <Input
              type="password"
              placeholder="••••••••••••••••••••••••••••••••"
              value={settings.subscriptionKey}
              onChange={(e) => handleChange('subscriptionKey', e.target.value)}
              className="font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              Το Subscription Key από το myDATA API
            </p>
          </div>

          {/* Test Connection Status */}
          {testStatus === 'testing' && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertDescription className="text-blue-800">
                🔄 Δοκιμή σύνδεσης με ΑΑΔΕ...
              </AlertDescription>
            </Alert>
          )}

          {testStatus === 'success' && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Επιτυχία!</strong> Η σύνδεση με την ΑΑΔΕ λειτουργεί σωστά.
              </AlertDescription>
            </Alert>
          )}

          {testStatus === 'error' && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Σφάλμα:</strong> {errorMessage}
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              onClick={testConnection}
              variant="outline"
              disabled={!settings.afm || !settings.userId || !settings.subscriptionKey || testStatus === 'testing'}
              className="flex-1"
            >
              Δοκιμή Σύνδεσης
            </Button>
            <Button
              onClick={handleSave}
              disabled={testStatus !== 'success'}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <Lock className="h-4 w-4 mr-2" />
              Αποθήκευση Κρυπτογραφημένα
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How to Get Credentials Card */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="text-lg">Πώς να αποκτήσετε τα credentials</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 rounded-full p-2 mt-1">
              <span className="font-bold text-blue-700">1</span>
            </div>
            <div>
              <p className="font-medium">Εγγραφή στο myDATA Developer Portal</p>
              <p className="text-gray-600">
                Επισκεφτείτε το{' '}
                <a 
                  href="https://www.aade.gr/mydata" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center"
                >
                  myDATA Portal <LinkIcon className="h-3 w-3 ml-1" />
                </a>
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 rounded-full p-2 mt-1">
              <span className="font-bold text-blue-700">2</span>
            </div>
            <div>
              <p className="font-medium">Δημιουργία API Credentials</p>
              <p className="text-gray-600">
                Μεταβείτε στο "Developer" → "Subscriptions" και δημιουργήστε ένα νέο subscription
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="bg-blue-100 rounded-full p-2 mt-1">
              <span className="font-bold text-blue-700">3</span>
            </div>
            <div>
              <p className="font-medium">Αντιγράψτε τα Credentials</p>
              <p className="text-gray-600">
                Αντιγράψτε το User ID και το Subscription Key και εισάγετέ τα παραπάνω
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features When Connected */}
      {isConnected && (
        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-900">Ενεργοποιημένες Δυνατότητες</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-green-800">
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Αυτόματη αποστολή τιμολογίων στην ΑΑΔΕ
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Λήψη MARK (Μοναδικός Αριθμός Καταχώρησης)
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Αυτόματη δημιουργία QR Code
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Real-time status tracking
              </li>
              <li className="flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Πλήρης συμμόρφωση με ελληνική νομοθεσία
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AADESettings;